import { Box } from '@mui/material'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline'
import React from 'react'
import InfoTable from './InfoTable'
import KioskHeartbeatChart from './KioskHeartbeatChart'
import { Restaurant } from '../utils/restaurant'

const EMPTY_HEARTBEATS: NonNullable<Restaurant['kioskHeartbeats']> = []

// Status links, Heartbeat-Diagramm rechts daneben; wird es zu schmal, rutscht das Diagramm darunter.
const sxLayout = { display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'flex-start' } as const
const sxStatusColumn = { flex: '1 1 30rem', maxWidth: '45rem' } as const
const sxChartColumn = { flex: '1 1 28rem', minWidth: 0 } as const

const sxStatus = { display: 'inline-flex', alignItems: 'center', gap: '0.4rem' } as const
const sxRestricted = { color: '#f9a825' } as const

// optional: fehlt der Punkt, ist das kein Fehler und wird grau statt rot angezeigt.
// restricted: ok, aber eingeschränkt (z. B. nur Testnutzer) — gelber Haken.
const StatusValue = React.memo(
	({
		ok,
		optional,
		restricted,
		text,
	}: {
		ok: boolean
		optional?: boolean
		restricted?: boolean
		text?: string
	}): JSX.Element => (
		<Box component="span" sx={sxStatus}>
			{ok ? (
				restricted ? (
					<CheckCircle fontSize="small" sx={sxRestricted} />
				) : (
					<CheckCircle color="success" fontSize="small" />
				)
			) : optional ? (
				<RemoveCircleOutline color="disabled" fontSize="small" />
			) : (
				<Cancel color="error" fontSize="small" />
			)}
			{text}
		</Box>
	)
)
StatusValue.displayName = 'StatusValue'

const RestaurantActivity = ({ restaurant }: { restaurant: Restaurant }): JSX.Element => {
	// Jede Voraussetzung fuer isActive einzeln, damit sichtbar ist, warum ein Restaurant nicht live
	// ist. Spiegelt api Restaurant.isActive — bitte synchron halten.
	const entries = React.useMemo(() => {
		const company = restaurant.company
		const testUserIds = restaurant.visibility ?? []
		const activeMenu = restaurant.menuHistory?.find((menu) => menu.state === 'ACTIVE')
		// Ohne aktive Zahlungsart oder bei DOOR/DOOR_TEST zahlen Kunden an der Tür.
		const paysOnline =
			restaurant.paymentMethod != null && !['DOOR', 'DOOR_TEST'].includes(restaurant.paymentMethod)
		return [
			{
				label: 'Onboarding-Stand',
				value: activeMenu
					? activeMenu.preview
						? 'Preview (Bestellungen werden zurückgehalten)'
						: 'Voll ongeboardet'
					: 'Kein aktives Menü',
				hint: [
					'Folgt der aktiven Menü-Version: Preview = ohne Vertrag, Barzahlung ohne Servicegebühr',
					'Preview-Bestellungen erreichen das Restaurant erst, wenn die Preview beendet wird',
				],
			},
			{
				label: 'Aktiv in der Mitbestellt-App',
				value: (
					<StatusValue
						ok={Boolean(restaurant.isActive)}
						restricted={testUserIds.length > 0 || restaurant.vacation}
						text={
							restaurant.isActive
								? [
										testUserIds.length
											? `nur für Testnutzer (${testUserIds.join(', ')})`
											: 'für alle',
										restaurant.vacation ? 'derzeit nicht buchbar (Urlaubsmodus)' : null,
								  ]
										.filter(Boolean)
										.join(' · ')
								: undefined
						}
					/>
				),
				hint: [
					'Ergebnis der Pflichtpunkte darunter',
					'Ist es Nein, liefert das Restaurant keine Lieferzeitpunkte aus und Buchungen werden abgelehnt',
					'Sind Testnutzer hinterlegt (visibility), sehen nur diese das Restaurant',
				],
			},
			{
				label: 'E-Mail-Adresse',
				value: <StatusValue ok={Boolean(restaurant.email)} />,
				hint: [
					'Restaurant-E-Mail',
					'Ohne sie gäbe es keinen Empfänger für die Bestellbenachrichtigungen',
				],
			},
			{
				label: 'Telefonnummer',
				value: <StatusValue ok={Boolean(restaurant.phone)} />,
				hint: ['Restaurant-Telefonnummer für die telefonische Bestellabwicklung'],
			},
			{
				label: 'Bei Mitbestellt aktiviert (is_ubimeal)',
				value: <StatusValue ok={Boolean(restaurant.isUbimeal)} />,
				hint: [
					'Manueller Schalter in der DB, kein Code setzt ihn',
					'Unsere Freigabe, dass das Restaurant live gehen darf',
				],
			},
			{
				label: 'Straße',
				value: <StatusValue ok={Boolean(restaurant.street)} />,
				hint: ['Teil der Restaurantanschrift, die im Vertrag und auf der Rechnung steht'],
			},
			{
				label: 'Hausnummer',
				value: <StatusValue ok={Boolean(restaurant.houseNumber)} />,
				hint: ['Teil der Restaurantanschrift, die im Vertrag und auf der Rechnung steht'],
			},
			{
				label: 'PLZ',
				value: <StatusValue ok={Boolean(restaurant.zipCode)} />,
				hint: ['Teil der Restaurantanschrift, die im Vertrag und auf der Rechnung steht'],
			},
			{
				label: 'USt-IdNr. oder Steuernummer',
				value: <StatusValue ok={Boolean(company?.ustId || company?.taxId)} />,
				hint: [
					'Eine der beiden Steuer-IDs der Firma muss hinterlegt sein — sie erscheint auf der Rechnung an den Kunden',
				],
			},
			{
				label: 'Aktives Menü veröffentlicht',
				value: <StatusValue ok={restaurant.menuVersion != null} />,
				hint: [
					'Es muss eine Menü-Version im Status ACTIVE geben',
					'Entwürfe und importierte Menüs zählen nicht',
				],
			},
			{
				label: 'Online-Zahlungsart aktiv (optional)',
				value: (
					<StatusValue
						ok={paysOnline}
						optional
						text={paysOnline ? 'Online-Zahlung' : 'Zahlung an der Tür'}
					/>
				),
				hint: [
					'Aktive Zahlungsart der Firma, etwa STRIPE oder STRIPE_TEST',
					'Ohne sie oder bei DOOR/DOOR_TEST zahlen Kunden Essen und Lieferung an der Tür, online nur die Servicegebühr',
				],
			},
			{
				label: 'Gemeinde zugeordnet',
				value: <StatusValue ok={Boolean(restaurant.communities?.length)} />,
				hint: [
					'Mindestens eine Gemeinde, in die das Restaurant liefert',
					'Kein Teil von Aktiv, ohne sie entstehen aber keine Lieferzeitpunkte',
				],
			},
			{
				label: 'Slot-Regeln festgelegt',
				value: <StatusValue ok={Boolean(restaurant.slots?.length)} />,
				hint: [
					'Wochentage und Uhrzeiten, aus denen die Lieferzeitpunkte erzeugt werden',
					'Kein Teil von Aktiv, ohne sie entstehen aber keine Lieferzeitpunkte',
				],
			},
			{
				label: 'Kein Urlaubsmodus',
				value: (
					<StatusValue
						ok={!restaurant.vacation}
						text={restaurant.vacation ? 'Im Urlaub' : undefined}
					/>
				),
				hint: [
					'Automatisch: an, wenn 15 Minuten kein Heartbeat kommt (das Restaurant bekommt eine Mail), aus mit dem nächsten Heartbeat',
					'Kein Teil von Aktiv: Im Urlaub sehen Kunden keine Lieferzeiten und können nicht bestellen',
					'Schon autorisierte oder bezahlte Bestellungen bleiben bestehen',
				],
			},
		]
	}, [restaurant])

	return (
		<Box sx={sxLayout}>
			<Box sx={sxStatusColumn}>
				<InfoTable title="Status" entries={entries} />
			</Box>
			<Box sx={sxChartColumn}>
				<KioskHeartbeatChart heartbeats={restaurant.kioskHeartbeats ?? EMPTY_HEARTBEATS} />
			</Box>
		</Box>
	)
}

export default React.memo(RestaurantActivity)
